<template>
	<div
		class="flow-logs-container flow-add-scrollbar bar-width-6 bar-height-6"
		:background="background"
	>
		<!-- eslint-disable-next-line vue/no-mutating-props -->
		<textarea ref="logs-to-format" v-model="content" readonly />
	</div>
</template>
<script>
import { defineComponent } from "vue";
/* eslint-disable @babel/no-unused-expressions */
/* eslint-disable no-sequences */
import "./codemirror-global";
import CodeMirror from "codemirror";
import "codemirror/addon/fold/foldcode";
import "codemirror/addon/fold/foldgutter";
import "codemirror/addon/fold/brace-fold";
import "codemirror/addon/fold/indent-fold";
import "codemirror/addon/search/searchcursor";
import "codemirror/addon/search/jump-to-line";
import "codemirror/mode/javascript/javascript";
import "codemirror/mode/shell/shell";

import "codemirror/lib/codemirror.css";
import "codemirror/addon/fold/foldgutter.css";

import initSearch from "./search";
import initDialog from "./dialog";

initSearch(CodeMirror);
initDialog(CodeMirror);

export default defineComponent({
	name: "Logs",

	props: {
		/**
		 * Content to show
		 */
		content: {
			type: String,
			default: null
		},
		/**
		 * Auto scroll when content is updated
		 */
		autoScroll: {
			type: Boolean,
			default: false
		},
		/**
		 * true | false
		 */
		wrap: {
			type: Boolean,
			default: true
		},
		/**
		 * true | false
		 */
		showSearch: {
			type: Boolean,
			default: false
		},
		/**
		 * true | false
		 */
		showLineNumber: {
			type: Boolean,
			default: true
		},
		/**
		 * 'javascript' | 'html' | 'css'
		 */
		contentType: {
			type: String,
			default: "shell"
		},
		/**
		 * override default options by passign object to this prop (Please refer CodeMirror documentation)
		 */
		extraOptions: {
			type: Object,
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			default: () => {}
		},
		background: {
			type: String,
			default: "default",
			validator(value) {
				return ["default", "element", "element-light", "element-dark"].includes(value);
			}
		}
	},
	data() {
		return {
			codeInstance: null,
			searchKeyword: null,
			noResults: false,
			enterPress: 0
		};
	},
	computed: {
		showNoResult: {
			get() {
				return this.searchKeyword !== null && this.searchKeyword !== "" && this.noResults;
			}
		},
		mode() {
			if (this.contentType === "json" || this.contentType === "javascript") {
				return { name: "javascript", json: true };
			}
			return this.contentType;
		}
	},
	watch: {
		/**
		 * watch for showSearch flag
		 */
		showSearch: {
			handler() {
				if (this.showSearch) {
					this.showSearchInput();
				} else {
					this.$el.querySelector("div.CodeMirror-dialog").remove();
				}
			}
		},
		/**
		 * watch for wrap flag
		 */
		wrap: {
			handler() {
				this.init();
			}
		}
	},
	mounted() {
		this.init();
	},
	methods: {
		init() {
			if (this.$el.querySelector("div.CodeMirror")) {
				this.$el.querySelector("div.CodeMirror").remove();
			}

			this.codeInstance = CodeMirror.fromTextArea(this.$refs["logs-to-format"], {
				mode: this.mode,
				lineNumbers: this.showLineNumber,
				lineWrapping: this.wrap,
				readOnly: true,
				openDialog(t, c, options) {
					// eslint-disable-next-line no-console
					console.log(t, c, options);
				},
				extraKeys: {
					"Ctrl-Q"(cm) {
						cm.foldCode(cm.getCursor());
					}
				},
				theme: "flow",
				foldGutter: true,
				gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"],
				foldOptions: {
					widget: (from, to) => {
						let count = undefined;

						// Get open / close token
						let startToken = "{",
							endToken = "}";
						const prevLine = this.codeInstance.getLine(from.line);
						if (prevLine.lastIndexOf("[") > prevLine.lastIndexOf("{")) {
							(startToken = "["), (endToken = "]");
						}

						// Get json content
						const internal = this.codeInstance.getRange(from, to);
						const toParse = startToken + internal + endToken;

						// Get key count
						try {
							const parsed = JSON.parse(toParse);
							count = Object.keys(parsed).length;
						} catch (e) {
							// eslint-disable-next-line no-console
							console.log(e);
						}

						return count ? `\u21A4${count}\u21A6` : "\u2194";
					}
				},
				...this.extraOptions
			});
			// console.log(this.codeInstance);

			// CodeMirror.autoLoadMode(this.codeInstance, this.contentType);
			const showSearchDialog = () => {
				// console.log("cmd-f pressed!");
				if (!this.$el.querySelector("input.CodeMirror-search-field")) {
					this.codeInstance.execCommand("findPersistent");
				}
			};
			this.codeInstance.addKeyMap({ "Cmd-F": showSearchDialog });
			this.codeInstance.addKeyMap({ "Ctrl-F": showSearchDialog });

			// console.log(this.codeInstance);
			if (this.showSearch) {
				this.showSearchInput();
			}
		},
		showSearchInput() {
			if (this.codeInstance && !this.$el.querySelector("input.CodeMirror-search-field")) {
				this.codeInstance.execCommand("findPersistent");
			}
		}
	}
});
</script>
<style lang="scss">
$backgrounds: (
	"default": var(--dashboard-backgrounds),
	"element": var(--dashboard-element),
	"element-light": var(--dashboard-element-light),
	"element-dark": var(--dashboard-element-dark)
);

div.flow-logs-container {
	display: flex;
	overflow: auto;
	max-height: 100%;
	height: 100%;
	// flex-direction: column;

	> textarea {
		width: 100%;
		height: 100%;
	}

	div.CodeMirror {
		display: flex;
		flex: 1;
		height: inherit;
		font-size: 12px;
		// height: 100%;
		flex-direction: column-reverse;
		font-family: var(--log-font);

		.CodeMirror-foldmarker {
			color: var(--dashboard-font-dark);
			text-shadow: none;
			font-family: arial;
			line-height: 0.3;
			cursor: pointer;
			font-weight: bold;
			border: 1px solid var(--dashboard-primary);
			border-radius: 2px;
			font-size: 10px;
			padding: 2px 6px 2px 6px;
			margin: 0px 4px 0px 4px;
			background-color: var(--dashboard-primary);
			letter-spacing: 1px;
		}

		div.CodeMirror-dialog.CodeMirror-dialog-top {
			padding: 16px;

			position: -webkit-sticky;
			position: sticky;
			top: 0;
			z-index: 5;
			input.CodeMirror-search-field {
				background-color: var(--dashboard-input);
				color: var(--dashboard-font-dark);
				width: 100% !important;
				font-size: 14px;
				font-family: inherit;
				font-weight: normal;
				line-height: 14px;
				padding: 8px;
				outline: none;
				padding-right: 36px;
				padding-left: 36px;
				border-radius: 18px;
				border-style: solid;
				box-sizing: border-box;

				border: none;
				flex: 1 1 auto;
				&:not([disabled]) {
					&:focus {
						outline: none;
						box-shadow: inset 0 0 0 2px var(--dashboard-primary);
					}
				}

				&::placeholder {
					font-size: 14px;
					font-weight: normal;
					line-height: 14px;
					box-sizing: border-box;
					color: var(--dashboard-input-placeholder);
				}
			}
			.CodeMirror-search-label {
				display: none;
			}
			.CodeMirror-search-hint {
				display: none;
			}

			svg#search-text {
				width: 16px;
				height: 16px;
				position: absolute;
				margin-left: 12px;
				margin-top: 9px;
				fill: var(--dashboard-font);
			}
		}

		*::-webkit-scrollbar-track {
			-webkit-box-shadow: inset 0 0 2px transparent;
			background-color: transparent;
		}

		*::-webkit-scrollbar {
			width: 6px;
			height: 6px;
			background-color: transparent;
			display: unset;
		}

		*::-webkit-scrollbar-thumb {
			background-color: var(--dashboard-scrollbar);
		}
		scrollbar-color: var(--dashboard-scrollbar) transparent;
		scrollbar-width: thin;
	}

	@each $bg, $color in $backgrounds {
		&[background="#{$bg}"] {
			.cm-s-flow.CodeMirror,
			.cm-s-flow .CodeMirror-gutters {
				background-color: $color;
			}
		}
	}
}

.cm-s-flow.CodeMirror {
	//	background-color: var(--dashboard-background);
	color: var(--dashboard-font);
}

.cm-s-flow .CodeMirror-gutters {
	//	background: var(--dashboard-background);
	color: var(--dashboard-font);
	border: none;
}

.cm-s-flow .CodeMirror-guttermarker,
.cm-s-flow .CodeMirror-guttermarker-subtle,
.cm-s-flow .CodeMirror-linenumber {
	color: var(--dashboard-font-light);
	// font-size: 10px;
}

.cm-s-flow .CodeMirror-linenumber {
	font-size: 10px;
}

.cm-s-flow .CodeMirror-cursor {
	border-left: 1px solid var(--dashboard-font-dark);
}

.cm-s-flow div.CodeMirror-selected {
	background: var(--dashboard-element-light);
}

.cm-s-flow.CodeMirror-focused div.CodeMirror-selected {
	// background: rgba(97, 97, 97, 0.2);
	background: var(--dashboard-element-light);
}

.cm-s-flow .CodeMirror-line::selection,
.cm-s-flow .CodeMirror-line > span::selection,
.cm-s-flow .CodeMirror-line > span > span::selection {
	background: rgba(128, 203, 196, 0.2);
}

.cm-s-flow .CodeMirror-line::-moz-selection,
.cm-s-flow .CodeMirror-line > span::-moz-selection,
.cm-s-flow .CodeMirror-line > span > span::-moz-selection {
	background: rgba(128, 203, 196, 0.2);
}

.cm-s-flow .CodeMirror-activeline-background {
	background: rgba(0, 0, 0, 0.5);
}

.cm-s-flow .cm-keyword {
	color: #c792ea;
}

.cm-s-flow .cm-operator {
	color: #89ddff;
}

.cm-s-flow .cm-variable-2 {
	color: #eeffff;
}

.cm-s-flow .cm-variable-3,
.cm-s-flow .cm-type {
	color: #f07178;
}

.cm-s-flow .cm-builtin {
	color: #ffcb6b;
}

.cm-s-flow .cm-atom {
	color: #f78c6c;
}

.cm-s-flow .cm-number {
	color: #ff5370;
}

.cm-s-flow .cm-def {
	color: #82aaff;
}

.cm-s-flow .cm-string {
	color: #c3e88d;
}

.cm-s-flow .cm-string-2 {
	color: #f07178;
}

.cm-s-flow .cm-comment {
	color: #545454;
}

.cm-s-flow .cm-variable {
	color: #f07178;
}

.cm-s-flow .cm-tag {
	color: #ff5370;
}

.cm-s-flow .cm-meta {
	color: #ffcb6b;
}

.cm-s-flow .cm-attribute {
	color: #c792ea;
}

.cm-s-flow .cm-property {
	color: #c792ea;
}

.cm-s-flow .cm-qualifier {
	color: #decb6b;
}

.cm-s-flow .cm-variable-3,
.cm-s-flow .cm-type {
	color: #decb6b;
}

.cm-s-flow .cm-error {
	color: rgba(255, 255, 255, 1);
	background-color: #ff5370;
}

.cm-s-flow .CodeMirror-matchingbracket {
	text-decoration: underline;
	color: white !important;
}
</style>
